// components/notifications/NotificationDropdown.tsx

'use client'

import Link from 'next/link'
import { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Bell, Loader2 } from 'lucide-react'
import { useNotifications } from '@/hooks/useNotifications'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useInstanceContext } from '@/lib/context/InstanceProvider'
import { useDeadlineNotices } from '@/lib/hooks/useDeadlineNotices'
import { hideActiveDeadlineReceipts } from '@/lib/notifications/deadlineNoticePresentation'
import { NotificationListItem } from './NotificationListItem'
import { cn } from '@/lib/utils'

const DROPDOWN_LIMIT = 5

export function NotificationDropdown() {
  const [open, setOpen] = useState(false)
  const { unread, isLoading, error, markRead, markAllRead, markReadPending, markAllReadPending } =
    useNotifications()
  const { user } = useAuth()
  const { currentInstance } = useInstanceContext()
  const { notices } = useDeadlineNotices(user?.id ?? null, currentInstance?.season_id ?? null)
  const otherUnread = hideActiveDeadlineReceipts(unread, notices)
  const attentionCount = otherUnread.length + notices.length

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications${attentionCount ? ` (${attentionCount} to review)` : ''}`}
          className="relative inline-flex items-center justify-center rounded-md p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <Bell className="h-5 w-5" />
          {attentionCount > 0 && (
            <span
              className={cn(
                'absolute top-1 right-1 inline-flex items-center justify-center',
                'min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none',
                'bg-red-500 text-white'
              )}
            >
              {attentionCount > 99 ? '99+' : attentionCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b dark:border-zinc-800">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {otherUnread.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.preventDefault()
                if (notices.length) {
                  void markRead(otherUnread.map((notification) => notification.id))
                } else {
                  void markAllRead()
                }
              }}
              disabled={markReadPending || markAllReadPending}
              className="h-7 text-xs"
            >
              {markReadPending || markAllReadPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : notices.length ? (
                'Mark others read'
              ) : (
                'Mark all read'
              )}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notices.length > 0 && (
            <Link
              href="/notifications"
              onClick={() => setOpen(false)}
              className="block border-b border-sky-500/30 bg-sky-500/10 px-3 py-3 text-sm hover:bg-sky-500/20"
            >
              <span className="font-semibold text-sky-400">Schedule updates</span>
              <span className="mt-1 block text-xs text-neu-text-primary">
                {notices.length} gameweek{notices.length === 1 ? '' : 's'} to review · View
                deadlines →
              </span>
            </Link>
          )}
          {error ? (
            <p className="px-3 py-6 text-center text-xs text-red-500">
              Failed to load. Try again later.
            </p>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          ) : otherUnread.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-zinc-500">
              {notices.length ? 'No other new notifications.' : 'No new notifications.'}
            </p>
          ) : (
            <ul className="divide-y dark:divide-zinc-800">
              {otherUnread.slice(0, DROPDOWN_LIMIT).map((n) => (
                <li key={n.id}>
                  <NotificationListItem notification={n} onMarkRead={(id) => markRead([id])} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t dark:border-zinc-800 p-2">
          <Link
            href="/notifications"
            onClick={() => setOpen(false)}
            className="block w-full text-center text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline py-1"
          >
            See all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
